import { useAppDispatch, useAppSelector } from "@app/hooks";
import { LSelect, SelectOption } from "@components/LSelect";
import { DefaultErrorMessages } from "@helpers/formValidationHelper";
import { yupResolver } from "@hookform/resolvers/yup";
import { useModal } from "@services/ModalContext";
import { useApi } from "@services/useApi";
import { RoleId } from "@store/generalTypes";
import { OrgLocation } from "@store/locationsSlice";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { SchemaOf, string, number, object } from "yup";
import { ExamineeAssignedTo, NewExamineeStepNames, updateExamineeAssignedTo } from "../../store/newExamineeSlice";
import { ExamineeCard } from "../ExamineeCard";

const formSchema: SchemaOf<ExamineeAssignedTo> = object({
    location: number()
        .transform((value) => (isNaN(value) ? 0 : value))
        .min(1, DefaultErrorMessages.required)
        .required(DefaultErrorMessages.required),
    clinicianId: string().required(DefaultErrorMessages.required)
});

const getLocations = (locations: OrgLocation[]): SelectOption[] => {
    return locations.map((x) => {
        return { name: x.name, value: x.id.toString() };
    });
};

export const ExamineeAssignedToComponent = (): JSX.Element => {
    const validateMethods = useForm<ExamineeAssignedTo>({
        resolver: yupResolver(formSchema)
    });

    const { setFormName, onConfirm } = useModal();
    const dispatch = useAppDispatch();

    const { getOrganizationUsers } = useApi();

    const contactInfo = useAppSelector((x) => x.newExaminee.contactInfo);
    const examineeDetails = useAppSelector((x) => x.newExaminee.examineeDetails);
    const locations = useAppSelector((x) => x.orgLocations.locations);
    const organization = useAppSelector((x) => x.organization);

    const onSubmit = async (data: ExamineeAssignedTo): Promise<void> => {
        dispatch(updateExamineeAssignedTo(data));

        onConfirm?.();
    };

    const [clinicians, setClinicians] = useState<SelectOption[]>([]);

    const handleGetOrganizationUsers = useCallback(async () => {
        const locationId = validateMethods.watch("location");

        const clinicianUsers = (await getOrganizationUsers(organization.id!, [locationId], [RoleId.Clinician])).items;

        const clinicianOptions = clinicianUsers.map((x) => {
            return { name: `${x.firstName} ${x.lastName}`, value: x.id };
        });

        setClinicians(clinicianOptions);
    }, [validateMethods.watch("location")]);

    useEffect(() => {
        if (validateMethods.watch("location")) {
            void handleGetOrganizationUsers();
        }
    }, [validateMethods.watch("location")]);

    useEffect(() => {
        const clinicianId = validateMethods.watch("clinicianId");
        if (clinicianId) {
            const locationId = validateMethods.watch("location");
            dispatch(updateExamineeAssignedTo({ location: locationId, clinicianId }));
        }
    }, [validateMethods.watch("clinicianId")]);

    useEffect(() => {
        setFormName?.(NewExamineeStepNames.AssignedTo);
    }, []);

    return (
        <div className="container has-text-ldark-gray pl-5">
            <div className="columns">
                <div className="column">
                    <p className="my-2 mb-5">Assign this examinee to a location & clinician</p>

                    <FormProvider {...validateMethods}>
                        <form id={NewExamineeStepNames.AssignedTo} onSubmit={validateMethods.handleSubmit(onSubmit)}>
                            <div className="mt-5">
                                <LSelect
                                    options={getLocations(locations)}
                                    label="Location"
                                    inputName="location"
                                    defaultOption={{ name: "Select location", value: "" } as SelectOption}
                                    defaultValue={undefined}
                                />
                                {clinicians.length ? (
                                    <div className="mt-5">
                                        <LSelect
                                            options={clinicians}
                                            label="Clinician"
                                            inputName="clinicianId"
                                            defaultOption={{ name: "Select clinician", value: "" } as SelectOption}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </form>
                    </FormProvider>
                </div>
                <div className="column">
                    <ExamineeCard
                        bgColor="#F0F7FA"
                        contactInfo={contactInfo}
                        examineeDetails={examineeDetails}
                        classList="p-6 mx-5"
                    />
                </div>
            </div>
        </div>
    );
};
